<template>
  <div class="certificate-container">
    <!-- Header -->
    <header class="certificate-header">
      <div class="header-bar">
        <a
          href="https://www.oxvalue.ai/"
          class="header-logo"
          title="OxValue.AI"
          target="_blank"
          data-image-width="575"
          data-image-height="136"
        >
          <img src="/images/logo1.png" class="logo-image" alt="OxValue.AI Logo" />
        </a>
        <button id="downloadButton" @click="downloadPDF">Download PDF</button>
      </div>
    </header>
    <img :src="certificateImageSrc" v-if="certificateImageSrc" />
    <!-- <CertificateFixed :certificateData="certificateData"></CertificateFixed> -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, watch, createApp, h } from "vue";
import { useRoute } from "vue-router";
import html2canvas from "html2canvas";
import html2pdf from "html2pdf.js";
import { formatNumberWithCommas, getFormattedDate } from "@/utils/certificateUtils";
import { chatApi } from "@/api/api";
import CertificateFixed from "@/views/Certificate/CertificateFixed.vue";

// Certificate fixed dimensions (based on image size)
const CERTIFICATE_WIDTH = 1785;
const CERTIFICATE_HEIGHT = 2526;

// Certificate data interface
interface CertificateData {
  companyName: string;
  valuationLower: string;
  valuationUpper: string;
  currency: string;
  date: string;
  certificateNumber: string;
  consultantName: string;
  companyType: number; // 1=general, 2=innovation, 8=patent, 9=bank
  showWatermark: boolean;
}

// API Response interfaces
interface ValuationResult {
  currency: string;
  lower_bound: number;
  upper_bound: number;
}

interface SubmissionItem {
  certificate_id: string;
  valuation_result: ValuationResult;
  valuation_log: {
    company_name: string;
  };
  create_time: string;
  report_type: string;
  config: {
    certification_id: string;
    case_name: string;
    consultant_name: string;
    [key: string]: any;
  };
}

interface SubmissionResponse {
  total: number;
  items: SubmissionItem[];
}

// Get route to access submission_id and valuation_subject from query parameters
const route = useRoute();
const submissionId = route.query.id as string;
const valuationSubject = route.query.subject as string;

// Loading state
const isLoading = ref(true);
const loadError = ref<string | null>(null);

// Certificate image
const certificateImageSrc = ref<string>("");

// Placeholder data
const certificateData = ref<CertificateData>({
  companyName: "Acme Technology Inc.",
  valuationLower: formatNumberWithCommas(19571271),
  valuationUpper: formatNumberWithCommas(26478778),
  currency: "GBP",
  date: getFormattedDate(new Date()),
  certificateNumber: "GB15012212070000234",
  consultantName: "Peng Xiong",
  companyType: 1, // 1=general, 2=innovation, 8=patent, 9=bank
  showWatermark: true,
});

// Fetch certificate data from API
const fetchCertificateData = async () => {
  try {
    isLoading.value = true;
    loadError.value = null;

    // Validate submission ID exists
    if (!submissionId) {
      loadError.value =
        "Missing submission ID. Please provide an ID in the URL (?id=your-submission-id)";
      isLoading.value = false;
      return;
    }

    const response = (await chatApi.getSubmissionDetail(submissionId)) as SubmissionResponse;

    if (response.items && response.items.length > 0) {
      const submissionData = response.items[0]!;
      const valuationResult = submissionData.valuation_result;

      // Get certification ID - prefer from config, fallback to certificate_id
      const certificationId =
        submissionData.config?.certification_id || submissionData.certificate_id;

      // Get company name - prefer from config.case_name, fallback to valuation_log.company_name
      const companyName =
        submissionData.config?.case_name ||
        submissionData.valuation_log?.company_name ||
        "Company Name";

      // Get consultant name from config or use default
      const consultantName = submissionData.config?.consultant_name || "Peng Xiong";

      // Determine company type based on valuation_subject from query parameter
      // STARTUP -> 1 (general), PATENT -> 8 (patent)
      let companyType = 1; // default to general
      if (valuationSubject === "PATENT") {
        companyType = 8;
      } else if (valuationSubject === "STARTUP") {
        companyType = 1;
      }

      // Update certificate data with API response
      certificateData.value = {
        ...certificateData.value,
        currency: valuationResult.currency,
        valuationLower: formatNumberWithCommas(valuationResult.lower_bound),
        valuationUpper: formatNumberWithCommas(valuationResult.upper_bound),
        companyName: companyName,
        certificateNumber: certificationId,
        date: getFormattedDate(new Date(submissionData.create_time)),
        consultantName: consultantName,
        companyType: companyType,
      };
    } else {
      loadError.value = "No submission data found";
    }
  } catch (error) {
    console.error("Error fetching certificate data:", error);
    loadError.value = error instanceof Error ? error.message : "Failed to load certificate data";
  } finally {
    isLoading.value = false;
  }
};

// Export certificate to image
const exportCertificateToImage = async () => {
  // Create temporary container for rendering
  const tempContainer = document.createElement("div");
  tempContainer.id = "temp-certificate-container";
  tempContainer.style.cssText = `
    position: absolute;
    left: -9999px;
    top: -9999px;
    width: ${CERTIFICATE_WIDTH}px;
    height: ${CERTIFICATE_HEIGHT}px;
    overflow: hidden;
  `;
  document.body.appendChild(tempContainer);

  // Create Vue app instance for temporary rendering
  const tempApp = createApp({
    setup: () => {
      return () =>
        h(CertificateFixed, {
          certificateData: certificateData.value,
        });
    },
  });

  // Mount the component to temporary container
  tempApp.mount(tempContainer);

  // Wait for component to be fully rendered
  await nextTick();

  // Wait a bit more for images and watermark to load
  await new Promise((resolve) => setTimeout(resolve, 800));

  const element = tempContainer.querySelector("#element-to-print") as HTMLElement;
  if (!element) {
    console.error("Certificate element not found");
    // Cleanup
    tempApp.unmount();
    document.body.removeChild(tempContainer);
    return;
  }

  try {
    // Force element to exact dimensions for export
    element.style.width = `${CERTIFICATE_WIDTH}px`;
    element.style.height = `${CERTIFICATE_HEIGHT}px`;
    element.style.transform = "none";
    element.style.margin = "0";
    element.style.boxSizing = "border-box";

    // Wait for style changes to apply
    await nextTick();
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Get actual element dimensions after forcing size
    const elementWidth = element.offsetWidth;
    const elementHeight = element.offsetHeight;

    console.log("Element dimensions after forcing:", elementWidth, elementHeight);
    console.log(`Expected dimensions: ${CERTIFICATE_WIDTH} x ${CERTIFICATE_HEIGHT}`);

    // Use html2canvas to convert certificate to image
    // Use actual element dimensions instead of fixed values
    const canvas = await html2canvas(element, {
      scale: 2, // High resolution
      width: elementWidth || CERTIFICATE_WIDTH, // Use actual width
      height: elementHeight || CERTIFICATE_HEIGHT, // Use actual height
      useCORS: true,
      logging: false,
      backgroundColor: null, // Transparent background
      allowTaint: false,
      windowWidth: elementWidth || CERTIFICATE_WIDTH,
      windowHeight: elementHeight || CERTIFICATE_HEIGHT,
    });

    console.log("Canvas dimensions:", canvas.width, canvas.height);
    console.log(
      "Expected canvas dimensions (with scale 2):",
      (elementWidth || CERTIFICATE_WIDTH) * 2,
      (elementHeight || CERTIFICATE_HEIGHT) * 2
    );

    // Convert canvas to base64 image
    const imageData = canvas.toDataURL("image/png", 1.0);
    certificateImageSrc.value = imageData;
  } catch (error) {
    console.error("Error exporting certificate to image:", error);
  } finally {
    // Cleanup: unmount app and remove temporary container
    tempApp.unmount();
    document.body.removeChild(tempContainer);
  }
};

// Watch for certificate data changes and export when ready
watch(
  () => certificateData.value.companyName,
  async (newVal) => {
    if (newVal && !isLoading.value && !loadError.value) {
      // Wait for component to render
      await nextTick();
      await exportCertificateToImage();
    }
  },
  { immediate: false }
);

// Download PDF function
const downloadPDF = async () => {
  if (!certificateImageSrc.value) {
    console.error("Certificate image not ready");
    return;
  }

  // Create temporary container for PDF generation
  const tempContainer = document.createElement("div");
  tempContainer.id = "temp-pdf-container";
  tempContainer.style.cssText = `
    position: absolute;
    left: -9999px;
    top: -9999px;
    width: ${CERTIFICATE_WIDTH}px;
    height: ${CERTIFICATE_HEIGHT}px;
    overflow: hidden;
  `;
  document.body.appendChild(tempContainer);

  // Create Vue app instance for temporary rendering
  const tempApp = createApp({
    setup: () => {
      return () =>
        h(CertificateFixed, {
          certificateData: certificateData.value,
        });
    },
  });

  // Mount the component to temporary container
  tempApp.mount(tempContainer);

  // Wait for component to be fully rendered
  await nextTick();
  await new Promise((resolve) => setTimeout(resolve, 800));

  const element = tempContainer.querySelector("#element-to-print") as HTMLElement;
  if (!element) {
    console.error("Certificate element not found");
    tempApp.unmount();
    document.body.removeChild(tempContainer);
    return;
  }

  try {
    // Force element to exact dimensions for PDF
    element.style.width = `${CERTIFICATE_WIDTH}px`;
    element.style.height = `${CERTIFICATE_HEIGHT}px`;
    element.style.transform = "none";
    element.style.margin = "0";
    element.style.boxSizing = "border-box";

    await nextTick();
    await new Promise((resolve) => setTimeout(resolve, 100));

    const options = {
      margin: [1.5, 1.5, 0.65, 0.2],
      filename: "certification.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        width: CERTIFICATE_WIDTH,
        height: CERTIFICATE_HEIGHT,
        useCORS: true,
        logging: false,
        windowWidth: CERTIFICATE_WIDTH,
        windowHeight: CERTIFICATE_HEIGHT,
      },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    await html2pdf().set(options).from(element).save();
  } catch (error) {
    console.error("PDF generation error:", error);
  } finally {
    // Cleanup: unmount app and remove temporary container
    tempApp.unmount();
    document.body.removeChild(tempContainer);
  }
};

onMounted(async () => {
  // Fetch certificate data first
  await fetchCertificateData();

  // Export to image after data is loaded
  if (!isLoading.value && !loadError.value) {
    await nextTick();
    await exportCertificateToImage();
  }
});
</script>

<style scoped>
.certificate-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f5f5f5;
  width: 100%;
  overflow-x: hidden;
}

.certificate-container img {
  width: 100%;
  max-width: 100%;
  height: auto;
  object-fit: contain;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: block;
  box-sizing: border-box;
}

/* Header */
.certificate-header {
  position: relative;
  z-index: 100;
  padding: 12px 16px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.header-logo {
  display: inline-flex;
  align-items: center;
}

.logo-image {
  max-height: 56px;
  width: auto;
  display: block;
}

#downloadButton {
  background: #478ac9;
  color: white;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  white-space: nowrap;
}

#downloadButton:hover {
  background: #3a7ab8;
}

/* Tighter header on phones */
@media (max-width: 767px) {
  .certificate-header {
    padding: 8px 12px;
  }

  .header-bar {
    gap: 10px;
  }

  .header-logo {
    margin: 0;
  }

  .logo-image {
    max-height: 30px;
    max-width: 110px;
    width: auto;
  }

  #downloadButton {
    padding: 6px 12px;
    font-size: 12px;
    height: auto;
    line-height: 1.2;
  }
}
</style>
